<template>
  <div class="wtk-chatbot-root">
    <!-- Launcher button — hidden off right edge, slides in on hover -->
    <div v-show="!hidden" class="wtk-launcher-zone" :class="{ 'wtk-launcher-zone--open': isOpen }">
      <button class="wtk-launcher" @click="toggleChat">
        <div v-show="showBadge" class="wtk-launcher-badge">1</div>
        <svg viewBox="0 0 24 24"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>
      </button>
    </div>

    <!-- Chat window -->
    <div class="wtk-chat-window" :class="{ 'wtk-open': isOpen }">
      <!-- Header -->
      <div class="wtk-chat-header">
        <div class="wtk-chat-header-top">
          <div>
            <div class="wtk-brand-row">
              <img :src="wtkLogo" alt="WTK" class="wtk-brand-img">
              <div>
                <div class="wtk-brand-name">Where to know</div>
                <div class="wtk-brand-status">
                  <span class="wtk-status-dot"></span> {{ $t('SupportChatbot.brandStatus') }}
                </div>
              </div>
            </div>
            <h2 class="wtk-h2">{{ $t('SupportChatbot.title') }}</h2>
            <p class="wtk-subtext">{{ $t('SupportChatbot.subtitle') }}</p>
          </div>
          <button class="wtk-close-btn" @click="toggleChat">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
              <path d="M1 1l12 12M13 1L1 13" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Messages area — populated imperatively to preserve all flow logic -->
      <div class="wtk-messages" ref="messages"></div>

      <!-- Input row -->
      <div class="wtk-chat-input-row">
        <input
          class="wtk-chat-input"
          ref="chatInput"
          type="text"
          :placeholder="$t('SupportChatbot.placeholder')"
          @keydown.enter="handleSend"
        >
        <button class="wtk-send-btn" @click="handleSend">
          <svg viewBox="0 0 24 24"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
        </button>
      </div>

      <div class="wtk-chat-footer">
        {{ $t('SupportChatbot.privacyPrefix') }} <a href="https://www.wheretoknow.com/html/en/PrivacyPolicy.html" target="_blank" rel="noopener noreferrer">{{ $t('SupportChatbot.privacyLink') }}</a>{{ $t('SupportChatbot.privacySuffix') }}
      </div>
    </div>
  </div>
</template>

<script>
import { onOverlayChange } from "@/utils/overlayWatcher.js";
import WTK_LOGO from "@/assets/img/logo.png";

// ---------------------------------------------------------------------------
// Conversation flow — 文案都在语言包 SupportChatbot 下：msg 是 SupportChatbot.msg 下的 key，
// label 是 SupportChatbot.opt 下的 key，渲染时再 $t（见 stepMsg / optLabel）。
// ---------------------------------------------------------------------------
const GOT_IT_OPTIONS = (back) => [
  { label: "gotIt", next: "resolved" },
  { label: "needMoreHelp", next: "connect_human" },
  { label: "back", next: back },
];

// 两个版本共用的步骤（开场、报错、转人工、结束语）
const FLOW_COMMON = {
  start: {
    msg: "start",
    options: [
      { label: "learnFeature", next: "feature_help" },
      { label: "contactSupport", next: "connect_human" },
      { label: "reportIssue", next: "report_bug" },
    ]
  },
  help_library: { msg: "library", options: GOT_IT_OPTIONS("feature_help") },
  help_reports: { msg: "reports", options: GOT_IT_OPTIONS("feature_help") },

  // Bug Report
  report_bug: {
    msg: "reportBug",
    options: [
      { label: "dataMissing", next: "bug_data" },
      { label: "crashError", next: "bug_other" },
      { label: "backToMain", next: "start" },
    ]
  },
  bug_data: {
    msg: "bugData",
    options: [
      { label: "resolvedIt", next: "resolved" },
      { label: "stillMissing", next: "connect_human" },
    ]
  },
  bug_other: {
    msg: "bugOther",
    options: [
      { label: "emailSupport", next: "connect_human" },
      { label: "back", next: "report_bug" },
    ]
  },

  // Connect Human
  connect_human: {
    type: "agent",
    msg: "connectHuman",
  },

  // End States
  resolved: {
    msg: "resolved",
    options: [
      { label: "anotherQuestion", next: "start" },
      { label: "thatsAll", next: "feedback" },
    ]
  },
  feedback: {
    msg: "feedback",
    options: [
      { label: "helpful", next: "feedback_positive" },
      { label: "notReally", next: "feedback_negative" },
    ]
  },
  feedback_positive: {
    msg: "feedbackPositive",
    options: []
  },
  feedback_negative: {
    msg: "feedbackNegative",
    type: "agent",
  },
};

// ---------------------------------------------------------------------------
// Conversation flow — Hotel version
// ---------------------------------------------------------------------------
const FLOW_HOTEL = {
  ...FLOW_COMMON,

  // Feature Help
  feature_help: {
    msg: "featureHelp",
    options: [
      { label: "wqi", next: "help_wtk_score" },
      { label: "hotelOverview", next: "help_dashboard" },
      { label: "hotelCompetitors", next: "help_competitors" },
      { label: "replySetting", next: "help_reply" },
      { label: "replyLibrary", next: "help_library" },
      { label: "needsAttention", next: "help_attention" },
      { label: "reports", next: "help_reports" },
      { label: "brandOverview", next: "help_brand" },
      { label: "settings", next: "help_settings" },
      { label: "backToMain", next: "start" },
    ]
  },
  help_wtk_score: { msg: "hotel.wqi", options: GOT_IT_OPTIONS("feature_help") },
  help_dashboard: { msg: "hotel.dashboard", options: GOT_IT_OPTIONS("feature_help") },
  help_competitors: {
    msg: "hotel.competitors",
    options: [
      { label: "gotIt", next: "resolved" },
      { label: "adjustCompSet", next: "connect_human" },
      { label: "back", next: "feature_help" },
    ]
  },
  help_reply: { msg: "hotel.reply", options: GOT_IT_OPTIONS("feature_help") },
  help_attention: { msg: "hotel.attention", options: GOT_IT_OPTIONS("feature_help") },
  help_brand: { msg: "hotel.brand", options: GOT_IT_OPTIONS("feature_help") },
  help_settings: { msg: "hotel.settings", options: GOT_IT_OPTIONS("feature_help") },
};

// ---------------------------------------------------------------------------
// Conversation flow — Restaurant version
// ---------------------------------------------------------------------------
const FLOW_RESTAURANT = {
  ...FLOW_COMMON,

  // Feature Help
  feature_help: {
    msg: "featureHelp",
    options: [
      { label: "wqi", next: "help_wtk_score" },
      { label: "restaurantOverview", next: "help_dashboard" },
      { label: "restaurantCompetitors", next: "help_competitors" },
      { label: "replySetting", next: "help_reply" },
      { label: "replyLibrary", next: "help_library" },
      { label: "needsAttention", next: "help_attention" },
      { label: "reports", next: "help_reports" },
      { label: "staffLeaderboard", next: "help_staff" },
      { label: "settings", next: "help_settings" },
      { label: "backToMain", next: "start" },
    ]
  },
  help_wtk_score: { msg: "restaurant.wqi", options: GOT_IT_OPTIONS("feature_help") },
  help_dashboard: { msg: "restaurant.dashboard", options: GOT_IT_OPTIONS("feature_help") },
  help_competitors: {
    msg: "restaurant.competitors",
    options: [
      { label: "gotIt", next: "resolved" },
      { label: "adjustCompSet", next: "connect_human" },
      { label: "back", next: "feature_help" },
    ]
  },
  help_reply: { msg: "restaurant.reply", options: GOT_IT_OPTIONS("feature_help") },
  help_attention: { msg: "restaurant.attention", options: GOT_IT_OPTIONS("feature_help") },
  help_staff: { msg: "restaurant.staff", options: GOT_IT_OPTIONS("feature_help") },
  help_settings: { msg: "restaurant.settings", options: GOT_IT_OPTIONS("feature_help") },
};

export default {
  name: 'SupportChatbot',

  data() {
    return {
      isOpen: false,
      showBadge: false,
      initialized: false,
      wtkLogo: WTK_LOGO,
      // Two independent signals OR'd together in the `hidden` computed below —
      // busDrawerOpen for the handful of drawers that explicitly opt out of
      // Element UI's modal mask (e.g. ReplySetting's `:modal="false"` drawer)
      // and still emit drawer:open/close themselves, modalOpen for literally
      // everything else (see modalOpen's own comment).
      busDrawerOpen: false,
      modalOpen: false,
      FLOW: FLOW_HOTEL, // Default to Hotel
    };
  },

  computed: {
    hidden() {
      return this.busDrawerOpen || this.modalOpen;
    },
  },

  mounted() {
    // Determine FLOW based on current route
    if (this.$route.path.includes('Restaurant')) {
      this.FLOW = FLOW_RESTAURANT;
    } else {
      this.FLOW = FLOW_HOTEL;
    }

    // Global rule: the launcher sits fixed at the bottom-right corner with a
    // z-index above every el-drawer/el-dialog's own, so it visually overlaps
    // the last thing anyone opens there — first found on Manage Sources
    // (ResponseRate.vue), then again on this Standards Exposure drawer. Fixing
    // it one drawer at a time (each remembering to $bus.$emit('drawer:open'))
    // doesn't scale and keeps getting missed. Element UI's PopupManager already
    // inserts a shared `.v-modal` mask into <body> for every dialog/drawer that
    // doesn't opt out with `:modal="false"` (nearly all of them, see the file
    // this comment lives in) — watching for that one class covers every
    // existing and future drawer/dialog with zero per-component wiring.
    //
    // 后来改成共用 overlayWatcher：除了 .v-modal，也认自己写的全屏遮罩
    // （各页面的底部筛选弹层等），和 Libredesk 挂件用同一套判断。
    this._offOverlay = onOverlayChange((open) => { this.modalOpen = open; });
  },

  created() {
    this.$bus.$on('drawer:open', () => { this.busDrawerOpen = true; });
    this.$bus.$on('drawer:close', () => { this.busDrawerOpen = false; });
  },

  beforeDestroy() {
    this.$bus.$off('drawer:open');
    this.$bus.$off('drawer:close');
    if (this._offOverlay) this._offOverlay();
  },

  methods: {
    // ── Open / Close ───────────────────────────────────────
    toggleChat() {
      this.isOpen = !this.isOpen;
      this.showBadge = false;
      if (this.isOpen && !this.initialized) {
        this.initialized = true;
        setTimeout(() => this.renderStep('start'), 400);
      }
    },

    // ── 文案 ───────────────────────────────────────────────
    stepMsg(key) {
      return this.$t(`SupportChatbot.msg.${key}`);
    },
    optLabel(key) {
      return this.$t(`SupportChatbot.opt.${key}`);
    },

    // ── DOM helpers ────────────────────────────────────────
    addBotMessage(text) {
      const msgs = this.$refs.messages;
      const el = document.createElement('div');
      el.className = 'wtk-msg wtk-bot';
      el.innerHTML = `
        <div class="wtk-msg-avatar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>
        </div>
        <div><div class="wtk-msg-bubble">${text.replace(/\n/g, '<br>')}</div></div>`;
      msgs.appendChild(el);
      msgs.scrollTop = msgs.scrollHeight;
    },

    addUserMessage(text) {
      const msgs = this.$refs.messages;
      const el = document.createElement('div');
      el.className = 'wtk-msg wtk-user';
      el.innerHTML = `<div class="wtk-msg-bubble">${text}</div>`;
      msgs.appendChild(el);
      msgs.scrollTop = msgs.scrollHeight;
    },

    addTyping() {
      const msgs = this.$refs.messages;
      const el = document.createElement('div');
      el.className = 'wtk-typing-indicator';
      el.id = 'wtk-typing';
      el.innerHTML = `
        <div class="wtk-msg-avatar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>
        </div>
        <div class="wtk-typing-dots"><span></span><span></span><span></span></div>`;
      msgs.appendChild(el);
      msgs.scrollTop = msgs.scrollHeight;
    },

    removeTyping() {
      const el = document.getElementById('wtk-typing');
      if (el) el.remove();
    },

    addOptions(options) {
      const msgs = this.$refs.messages;
      const group = document.createElement('div');
      group.className = 'wtk-options-group';
      options.forEach(opt => {
        const btn = document.createElement('button');
        btn.className = 'wtk-opt-btn';
        const label = this.optLabel(opt.label);
        btn.textContent = label;
        btn.onclick = () => {
          this.addUserMessage(label);
          group.remove();
          setTimeout(() => this.renderStep(opt.next), 300);
        };
        group.appendChild(btn);
      });
      msgs.appendChild(group);
      msgs.scrollTop = msgs.scrollHeight;
    },

    addAgentCard() {
      const msgs = this.$refs.messages;
      const card = document.createElement('div');
      card.className = 'wtk-agent-card';
      card.innerHTML = `
        <h4>${this.$t('SupportChatbot.contactTitle')}</h4>
        <p>${this.$t('SupportChatbot.contactBody')}</p>
        <button class="wtk-agent-email-btn" onclick="window.location.href='mailto:info@wheretoknow.com?subject=Support%20Request'">
          \u2709\uFE0E&ensp;info@wheretoknow.com
        </button>`;
      msgs.appendChild(card);
      setTimeout(() => {
        this.addOptions([{ label: "backToMain", next: "start" }]);
      }, 200);
      msgs.scrollTop = msgs.scrollHeight;
    },

    renderStep(key) {
      const step = this.FLOW[key];
      if (!step) return;

      this.addTyping();
      const delay = 600 + Math.random() * 400;

      setTimeout(() => {
        this.removeTyping();
        this.addBotMessage(this.stepMsg(step.msg));
        setTimeout(() => {
          if (step.type === 'agent') {
            this.addAgentCard();
          } else if (step.options && step.options.length > 0) {
            this.addOptions(step.options);
          }
        }, 200);
      }, delay);
    },

    // ── Send ───────────────────────────────────────────────
    handleSend() {
      const input = this.$refs.chatInput;
      const text = (input.value || '').trim();

      if (!text) return;
      input.value = '';
      this.addUserMessage(text);

      // Keyword matching for free-text input
      const lower = text.toLowerCase();
      setTimeout(() => {
        // 'wtk score' 是改名前的叫法，留着 —— 老用户还会这么问，认不出来就把人晾在那了。
        if (lower.includes('wqi') || lower.includes('wtk score') || lower.includes('score') || lower.includes('rating')) {
          this.renderStep('help_wtk_score');
        } else if (lower.includes('library') || lower.includes('saved') || lower.includes('favorite') || lower.includes('favourite')) {
          this.renderStep('help_library');
        } else if (lower.includes('attention') || lower.includes('flagged') || lower.includes('urgent') || lower.includes('priority')) {
          this.renderStep('help_attention');
        } else if (lower.includes('brand') || lower.includes('ranking') || lower.includes('group')) {
          this.renderStep(this.FLOW.help_brand ? 'help_brand' : 'help_staff');
        } else if (lower.includes('notification') || lower.includes('alert') || lower.includes('threshold')) {
          this.renderStep('help_settings');
        } else if (lower.includes('review') || lower.includes('reply') || lower.includes('respond')) {
          this.renderStep('help_reply');
        } else if (lower.includes('dashboard') || lower.includes('overview') || lower.includes('kpi') || lower.includes('analytics') || lower.includes('staff') || lower.includes('leaderboard') || lower.includes('mention') || lower.includes('social media')) {
          this.renderStep('help_dashboard');
        } else if (lower.includes('competitor') || lower.includes('competition') || lower.includes('comp set')) {
          this.renderStep('help_competitors');
        } else if (lower.includes('report')) {
          this.renderStep('help_reports');
        } else if (lower.includes('goal') || lower.includes('setting') || lower.includes('password')) {
          this.renderStep('help_settings');
        } else if (lower.includes('bug') || lower.includes('error') || lower.includes('broken') || lower.includes('crash') || lower.includes('not working')) {
          this.renderStep('report_bug');
        } else if (lower.includes('human') || lower.includes('agent') || lower.includes('person') || lower.includes('contact') || lower.includes('email')) {
          this.renderStep('connect_human');
        } else if (lower.includes('faq') || lower.includes('question') || lower.includes('help')) {
          this.renderStep('feature_help');
        } else {
          this.addTyping();
          setTimeout(() => {
            this.removeTyping();
            this.addBotMessage(this.$t('SupportChatbot.noMatch'));
            setTimeout(() => this.addOptions(this.FLOW.start.options), 200);
          }, 700);
        }
      }, 300);
    },

  },
};
</script>

<!--
  NOT scoped — messages are created via document.createElement so scoped
  data-attributes won't reach them. All selectors are prefixed wtk- to
  prevent collision with the host app's styles.
-->
<style>
/* ── Containment reset ────────────────────────────────────── */
.wtk-chatbot-root *,
.wtk-chatbot-root *::before,
.wtk-chatbot-root *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Dynamically-created nodes also need the reset */
.wtk-msg *, .wtk-options-group *, .wtk-typing-indicator *,
.wtk-agent-card *, .wtk-msg-bubble, .wtk-opt-btn {
  box-sizing: border-box;
}

/* ── Launcher hover zone ──────────────────────────────────── */
/* Tucked mostly off the right edge at rest — clearly visible as a button
   (unlike the original, which left only ~22px of 52px on screen), sliding
   fully into view on hover. The zone spans edge-to-past-the-revealed-button
   so the mouse never leaves it while hovering either state, avoiding flicker
   at the boundary between "peeking" and "fully out." */
.wtk-launcher-zone {
  position: fixed;
  bottom: 16px;
  right: 0;
  width: 70px;
  height: 56px;
  z-index: 2100;
  cursor: pointer;
}
.wtk-launcher-zone .wtk-launcher {
  position: absolute;
  bottom: 4px;
  right: -24px;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--biz-color);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(28, 25, 23, 0.12);
  transition: right .25s ease, opacity .25s ease, box-shadow .25s ease;
  opacity: 0.85;
}
.wtk-launcher-zone:hover .wtk-launcher {
  right: 12px;
  opacity: 1;
  box-shadow: 0 8px 24px rgba(28, 25, 23, 0.18);
}
/* Chat open — stay fully out (this is now the "close" control the user is
   actively meant to see), not tucked away. */
.wtk-launcher-zone--open {
  width: 76px;
  height: 68px;
  bottom: 12px;
}
.wtk-launcher-zone--open .wtk-launcher {
  right: 12px;
  bottom: 8px;
  width: 52px;
  height: 52px;
  opacity: 1;
}
.wtk-launcher svg { width: 24px; height: 24px; fill: white; }
.wtk-launcher-badge {
  position: absolute;
  top: -4px; right: -4px;
  width: 18px; height: 18px;
  background: #D54941;
  border-radius: 50%;
  border: 2px solid #F5F2EA;
  font-size: 10px;
  font-weight: 600;
  color: white;
  display: flex; align-items: center; justify-content: center;
}

/* ── Chat window ──────────────────────────────────────────── */
.wtk-chat-window {
  position: fixed;
  bottom: 88px;
  right: 28px;
  width: 360px;
  max-height: 580px;
  background: #FCFBF8;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 8px 24px rgba(28, 25, 23, 0.12), 0 2px 8px rgba(28, 25, 23, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2099;
  transform: scale(0.95) translateY(12px);
  opacity: 0;
  pointer-events: none;
  transition: transform .2s ease, opacity .15s ease;
}
.wtk-chat-window.wtk-open {
  transform: scale(1) translateY(0);
  opacity: 1;
  pointer-events: all;
}

/* ── Header ───────────────────────────────────────────────── */
.wtk-chat-header {
  background: var(--biz-color);
  padding: 20px 20px 18px;
  color: white;
  flex-shrink: 0;
}
.wtk-chat-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.wtk-brand-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.wtk-brand-img {
  width: 36px; height: 36px;
  border-radius: 8px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  flex-shrink: 0;
}
.wtk-brand-name {
  font-size: 14px !important;
  font-weight: 600;
  opacity: 0.95;
  color: white !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-brand-status {
  font-size: 11px !important;
  opacity: 0.65;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
  color: white !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-status-dot {
  width: 6px; height: 6px;
  background: #4ade80;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.wtk-h2 {
  font-size: 18px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
  margin-bottom: 4px !important;
  color: white !important;
  font-family: 'Inter', sans-serif !important;
  letter-spacing: -0.01em;
}
.wtk-subtext {
  font-size: 13px !important;
  opacity: 0.7;
  line-height: 1.4 !important;
  color: white !important;
  margin: 0 !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-close-btn {
  background: rgba(255,255,255,0.12);
  border: none;
  color: white;
  cursor: pointer;
  width: 28px; height: 28px;
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
  flex-shrink: 0;
}
.wtk-close-btn:hover { background: rgba(255,255,255,0.2); }

/* ── Messages area ────────────────────────────────────────── */
.wtk-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #FCFBF8;
  scrollbar-width: thin;
  scrollbar-color: #D1D5DB transparent;
}
.wtk-messages::-webkit-scrollbar { width: 4px; }
.wtk-messages::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 2px; }

/* ── Message bubbles ──────────────────────────────────────── */
.wtk-msg {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  animation: wtk-fadeUp .2s ease forwards;
}
@keyframes wtk-fadeUp {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.wtk-user { flex-direction: row-reverse; }
.wtk-msg-avatar {
  width: 28px; height: 28px;
  border-radius: 8px;
  background: var(--biz-color);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.wtk-msg-bubble {
  max-width: 240px;
  padding: 10px 13px;
  border-radius: 12px;
  font-size: 13px !important;
  line-height: 1.5 !important;
  color: #1C1917 !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-bot .wtk-msg-bubble {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-bottom-left-radius: 4px;
}
.wtk-user .wtk-msg-bubble {
  background: var(--biz-color);
  border: none;
  color: white !important;
  border-bottom-right-radius: 4px;
}

/* ── Option buttons ───────────────────────────────────────── */
.wtk-options-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 36px;
  animation: wtk-fadeUp .2s ease forwards;
}
.wtk-opt-btn {
  background: #FFFFFF !important;
  border: 1px solid #D1D5DB !important;
  border-radius: 8px !important;
  padding: 9px 14px !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: var(--biz-text) !important;
  cursor: pointer;
  text-align: left;
  transition: border-color .15s, background .15s;
  display: flex;
  align-items: center;
  width: 100%;
  box-shadow: none !important;
}
.wtk-opt-btn:hover {
  background: #F0F2F5 !important;
  border-color: var(--biz-color) !important;
}

/* ── Typing indicator ─────────────────────────────────────── */
.wtk-typing-indicator {
  display: flex;
  gap: 8px;
  align-items: center;
  padding-left: 36px;
}
.wtk-typing-dots {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  gap: 4px;
  align-items: center;
}
.wtk-typing-dots span {
  width: 5px; height: 5px;
  background: #9CA3AF;
  border-radius: 50%;
  animation: wtk-bounce .9s infinite;
  display: inline-block;
}
.wtk-typing-dots span:nth-child(2) { animation-delay: .15s; }
.wtk-typing-dots span:nth-child(3) { animation-delay: .3s; }
@keyframes wtk-bounce {
  0%,80%,100% { transform: translateY(0); }
  40%          { transform: translateY(-4px); }
}

/* ── Agent card ───────────────────────────────────────────── */
.wtk-agent-card {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 14px;
  margin-left: 36px;
  animation: wtk-fadeUp .2s ease forwards;
}
.wtk-agent-card h4 {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--biz-text) !important;
  margin-bottom: 6px !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-agent-card p {
  font-size: 12.5px !important;
  color: #6B7280 !important;
  line-height: 1.45 !important;
  margin: 0 !important;
  font-family: 'Inter', sans-serif !important;
}
.wtk-agent-email-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px !important;
  background: var(--biz-color) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 14px !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  cursor: pointer;
  transition: background .15s;
  box-shadow: none !important;
  letter-spacing: 0;
}
.wtk-agent-email-btn:hover { background: #15294A !important; }

/* ── Input row ────────────────────────────────────────────── */
.wtk-chat-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #ECE7DE;
  background: #FCFBF8;
  flex-shrink: 0;
}
.wtk-chat-input {
  flex: 1;
  border: 1px solid #E5E7EB !important;
  border-radius: 8px !important;
  padding: 9px 13px !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
  color: #1C1917 !important;
  outline: none !important;
  transition: border-color .15s;
  background: #FFFFFF !important;
  box-shadow: none !important;
  height: auto !important;
}
.wtk-chat-input:focus {
  border-color: var(--biz-color) !important;
  background: #FFFFFF !important;
  box-shadow: none !important;
}
.wtk-chat-input::placeholder { color: #9CA3AF; }
.wtk-send-btn {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: var(--biz-color);
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
  flex-shrink: 0;
}
.wtk-send-btn:hover { background: #15294A; }
.wtk-send-btn svg { width: 16px; height: 16px; fill: white; }

/* ── Footer ───────────────────────────────────────────────── */
.wtk-chat-footer {
  text-align: center;
  font-size: 11px !important;
  color: #9CA3AF !important;
  padding: 0 14px 12px;
  flex-shrink: 0;
  font-family: 'Inter', sans-serif !important;
  background: #FCFBF8;
}
.wtk-chat-footer a { color: #9CA3AF !important; text-decoration: underline; }
</style>
